import { api } from "@/lib/axios"
import type { Customer, CustomersResponse, CustomerDetailResponse, CreateCustomerPayload, UpdateCustomerPayload } from "@/types/customers"
import type { CreateExpensePayload, CreateExpenseResponse } from "@/types/company-expenses"

export interface CustomerOrder {
  id: number
  full_price: number
  created_at: string
}

export interface CustomerOrdersResponse {
  status: number
  message: string
  data: CustomerOrder[]
}

export interface CustomerBoughtProduct {
  id: number
  thickness: string
  cell: number
  height: number
  width: number
  quantity: number
  price: number
  weight: number
  created_at: string
}

export interface CustomerBoughtProductsResponse {
  status: number
  message: string
  data: CustomerBoughtProduct[]
}

export async function fetchCustomers(): Promise<Customer[]> {
  const { data } = await api.get<CustomersResponse>("/customers/")
  return data.data
}

export async function fetchCustomerDetail(id: number): Promise<Customer> {
  const { data } = await api.get<CustomerDetailResponse>(`/customers/${id}/`)
  return data.data
}

export async function createCustomer(payload: CreateCustomerPayload): Promise<Customer> {
  const { data } = await api.post<CustomerDetailResponse>("/customers/", payload)
  return data.data
}

export async function updateCustomer(id: number, payload: UpdateCustomerPayload): Promise<Customer> {
  const { data } = await api.patch<CustomerDetailResponse>(`/customers/${id}/`, payload)
  return data.data
}

export async function deleteCustomer(id: number): Promise<void> {
  await api.delete(`/customers/${id}/`)
}

export async function fetchCustomerOrders(customerId: number): Promise<CustomerOrder[]> {
  const { data } = await api.get<CustomerOrdersResponse>(`/customers/orders/${customerId}/`)
  return data.data
}

export async function fetchCustomerBoughtProducts(customerId: number): Promise<CustomerBoughtProduct[]> {
  const { data } = await api.get<CustomerBoughtProductsResponse>(`/customers/bought-products/${customerId}/`)
  return data.data
}

export async function createCustomerIncome(
  customerId: number,
  payload: Omit<CreateExpensePayload, 'revenue_type' | 'expense_type' | 'client'>
): Promise<CreateExpenseResponse> {
  const expensePayload: CreateExpensePayload = {
    ...payload,
    revenue_type: 'income',
    expense_type: 'client',
    client: customerId,
  }
  const { data } = await api.post<CreateExpenseResponse>('/admin/company-expenses/', expensePayload)
  return data
}

export async function createCustomerDebt(
  customerId: number,
  payload: Omit<CreateExpensePayload, 'revenue_type' | 'expense_type' | 'client'>
): Promise<CreateExpenseResponse> {
  const expensePayload: CreateExpensePayload = {
    ...payload,
    revenue_type: 'debt',
    expense_type: 'client',
    client: customerId,
  }
  const { data } = await api.post<CreateExpenseResponse>('/admin/company-expenses/', expensePayload)
  return data
}
